import React from "react";
import { Link, useLocation } from "react-router-dom";
import { eventCategories, galleryImages } from "../mock";
import { ArrowRight, Check, Camera, Users, Heart } from "lucide-react";

const contentMap = {
  weddings: {
    title: "Weddings",
    tag: "For your one perfect day",
    description: "Capture every angle of your wedding day from your guests\u2019 perspective. From the first kiss to the dancefloor chaos \u2014 your private gallery has it all.",
    image: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    features: ["Custom QR code & signage", "Branded gallery", "Live slideshow at reception", "Original-resolution downloads", "Digital guestbook messages"]
  },
  party: {
    title: "Parties",
    tag: "Birthdays, anniversaries & big nights",
    description: "Whether it's a milestone birthday or a backyard bash, collect every blurry dancefloor shot in one private gallery.",
    image: "https://images.unsplash.com/photo-1699730164892-d7c433524ff3?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    features: ["Unlimited guests", "Easy QR scan", "Photo + video uploads", "Private gallery link", "Bulk HD download"]
  },
  business: {
    title: "Business Events",
    tag: "Conferences, launches & corporate gatherings",
    description: "Collect branded content from every angle of your event. Perfect for conferences, product launches, team off-sites and gala nights.",
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    features: ["Branded landing page", "Multi-album organization", "Moderation controls", "Team admin access", "Analytics dashboard"]
  },
  memorials: {
    title: "Memorials",
    tag: "Celebrate a life beautifully shared",
    description: "Bring family and friends together to share their treasured memories, photos and messages in one safe, private space.",
    image: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    features: ["Private & respectful", "Long-term gallery hosting", "Guestbook tributes", "Easy family sharing", "Original quality downloads"]
  },
  engagements: {
    title: "Engagements",
    tag: "From the proposal to the party",
    description: "Document every moment from the surprise to the celebration. Your guests will capture the candid shots you never knew existed.",
    image: "https://images.unsplash.com/photo-1606800052052-a08af7148866?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    features: ["Beautiful save-the-date kits", "Engagement timeline", "Real-time slideshow", "Sharable highlights", "Cloud storage"]
  },
  "kids-parties": {
    title: "Kids Parties",
    tag: "Tiny humans, big memories",
    description: "Collect all the icing-covered, balloon-popping, candle-blowing magic from every parent's phone in one cute, private gallery.",
    image: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
    features: ["Playful themes", "Privacy first", "Parent uploads", "Easy sharing with family", "Memory keepsake album"]
  }
};

const EventPage = () => {
  // Routes are static ("/weddings", "/party", "/business", "/memorials",
  // "/engagements", "/kids-parties"), NOT dynamic — so useParams() returns
  // an empty object here. Derive the key from the pathname instead.
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const data = contentMap[slug] || contentMap.weddings;

  return (
    <>
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
        <img src={data.image} alt={data.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/30 via-navy-900/40 to-navy-900/70" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 h-full flex flex-col justify-end pb-16 text-cream-100">
          <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">{data.tag}</p>
          <h1 className="font-serif italic text-5xl md:text-7xl mt-3">{data.title}</h1>
        </div>
      </section>

      <section className="section bg-cream-100">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-navy-700 text-4xl md:text-5xl leading-tight">A private gallery for every <span className="italic">{data.title.toLowerCase()}</span></h2>
            <p className="mt-5 text-navy-700/75 text-lg leading-relaxed">{data.description}</p>
            <ul className="mt-8 space-y-3">
              {data.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-navy-700"><Check className="w-5 h-5 text-coral-300 mt-0.5" /><span>{f}</span></li>
              ))}
            </ul>
            <div className="mt-8 flex gap-4 flex-wrap">
              <Link to="/get-started" className="bg-coral-300 text-white px-7 py-3.5 rounded-full font-semibold hover:bg-coral-400 transition-colors inline-flex items-center gap-2">Get Started <ArrowRight className="w-4 h-4" /></Link>
              <Link to={`/${slug}-pricing`.replace("--", "-")} className="border-2 border-navy-700 text-navy-700 px-7 py-3.5 rounded-full font-semibold hover:bg-navy-700 hover:text-white transition-colors">See Pricing</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {galleryImages.slice(0, 4).map((g, i) => (
              <div key={g} className={`overflow-hidden rounded-2xl ${i % 2 ? "mt-8" : ""} aspect-[3/4]`}>
                <img src={g} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-cream-200/50">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <h2 className="text-center font-serif text-navy-700 text-4xl md:text-5xl">Why hosts <span className="italic">love</span> GALERIECOP</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Camera, title: "Capture every angle", desc: "Hundreds of photos and videos from your guests \u2014 all in one place." },
              { icon: Users, title: "No app, no fuss", desc: "Guests just scan a QR code and upload \u2014 zero downloads required." },
              { icon: Heart, title: "Memories forever", desc: "Download everything in original resolution whenever you're ready." }
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-7 soft-shadow text-center">
                <div className="w-12 h-12 rounded-xl bg-coral-300/20 text-coral-300 flex items-center justify-center mx-auto"><Icon className="w-6 h-6" /></div>
                <h3 className="mt-5 font-serif italic text-2xl text-navy-700">{title}</h3>
                <p className="mt-3 text-navy-700/70 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-cream-100">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <h2 className="text-center font-serif text-navy-700 text-3xl md:text-4xl mb-10">Explore other events</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {eventCategories.filter(c => c.href !== `/${slug}`).map((c) => (
              <Link key={c.id} to={c.href} className="group relative aspect-square rounded-2xl overflow-hidden block">
                <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-navy-900/40 group-hover:bg-navy-900/20 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center text-white font-serif italic text-xl">{c.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default EventPage;
